import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {AsyncPipe} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {combineLatest, map, shareReplay, startWith, Subject, switchMap, takeUntil} from 'rxjs';

import {CardComponent} from '../../components/ui/card/card.component';
import {InputComponent} from '../../components/ui/input/input.component';
import {Column, TableComponent} from '../../components/ui/table/table.component';
import {SelectComponent} from '../../components/ui/select/select.component';
import {PointCloudService} from '../../services/point-cloud.service';
import {Sort} from '../../models/sort';
import {SortHelper} from '../../utils/sort-helper';
import {HttpParams} from '@angular/common/http';

@Component({
  selector: 'app-unmatched-point-clouds',
  standalone: true,
  imports: [
    CardComponent,
    InputComponent,
    TableComponent,
    TranslateModule,
    SelectComponent,
    ReactiveFormsModule,
    AsyncPipe
  ],
  templateUrl: './unmatched-point-clouds.component.html',
  styleUrl: './unmatched-point-clouds.component.scss'
})
export class UnmatchedPointCloudsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  public columns: Column[] = [
    { name: 'id', label: 'page.unmatched-point-clouds.list.id' },
    { name: 'start', label: 'page.unmatched-point-clouds.list.start', date: true },
    { name: 'end', label: 'page.unmatched-point-clouds.list.end', date: true },
    { name: 'duration', label: 'page.unmatched-point-clouds.list.duration', suffix: 'page.unmatched-point-clouds.list.duration.suffix' },
    { name: 'uploadedBy.email', label: 'page.unmatched-point-clouds.list.uploaded-by' },
    { name: 'uploadedAt', label: 'page.unmatched-point-clouds.list.uploaded-at', date: true },
  ];

  public sortControl = new FormControl('');

  public sortOptions = [
    { value: 'uploadedAt,desc', labelKey: 'table.sort.uploaded-at.desc' },
    { value: 'uploadedAt,asc', labelKey: 'table.sort.uploaded-at.asc' },
    { value: 'start,desc', labelKey: 'table.sort.start.desc' },
    { value: 'start,asc', labelKey: 'table.sort.start.asc' },
    { value: 'end,desc', labelKey: 'table.sort.end.desc' },
    { value: 'end,asc', labelKey: 'table.sort.end.asc' },
  ];

  public data$;

  constructor(
    private pointCloudService: PointCloudService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    // Initialize controls with query params or defaults BEFORE creating data$
    const queryParams = this.route.snapshot.queryParams;
    this.sortControl.setValue(queryParams['sort'] || 'uploadedAt,desc', { emitEvent: false });

    this.data$ = combineLatest([
      this.route.queryParams,
      this.sortControl.valueChanges.pipe(startWith(this.sortControl.value))
    ]).pipe(
      switchMap(([queryParams, sortValue]) => {
        let requestParams = new HttpParams();

        let page = 0;
        if (queryParams['page']) {
          page = +queryParams['page'];
        }

        let sort: Sort | undefined = undefined;
        if (sortValue) {
          sort = SortHelper.convertStringToSort(sortValue);
        }

        return this.pointCloudService.listUnmatched(page, sort, requestParams);
      }),
      map(response => ({
        ...response,
        content: response.content.map(pointCloud => ({
          ...pointCloud,
          link: `/panel/point-clouds/unmatched/detail/${pointCloud.localId}`,
        }))
      })),
      shareReplay(1)
    );
  }

  ngOnInit() {
    // Initialize form controls with query parameters
    this.route.queryParams.pipe(
      takeUntil(this.destroy$)
    ).subscribe(params => {
      if (params['sort'] && params['sort'] !== this.sortControl.value) {
        this.sortControl.setValue(params['sort'], { emitEvent: false });
      }
    });

    this.sortControl.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(sort => {
      this.router.navigate([], {
        queryParams: { sort, page: null },
        queryParamsHandling: 'merge',
      });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  changePage(page: number) {
    this.router.navigate([], {
      queryParams: { page },
      queryParamsHandling: 'merge',
    });
  }
}
